/**
 * PATH: packages/email-hub/src/feature/sender-identity/SenderSignalBadges.tsx
 * FILE_ID: EMAILHUB-FEATURE-SENDER-SIGNALS-001
 * PURPOSE: Surface independent relay and automated sender signals.
 *
 * NOTES FOR FUTURE AI: These chips must name a CONSEQUENCE, never restate the
 * detection. `signals.automated` is set by `is_transactional_sender` on the
 * Django side, which is an address-shape match (no-reply@, notifications@, ESP
 * relay domains). Labelling that "Automated" told the operator that
 * no-reply@gust.com looks automated, next to a body already saying "this is an
 * automated message" — pure restatement, and it read as clutter (operator,
 * 2026-07-26, change #334).
 *
 * The wording must not imply the email is skipped. Verified against
 * origin/production: the flag gates exactly two non-content priors — the +40
 * sender→job history boost (email_sender_history.py, email_job_suggestion_
 * resolvers.py) and the thread/sibling inheritance bypass (email_job_
 * suggestion_batch.py). Content scoring, verbatim job-number extraction,
 * invoice and verification-code detection, micro-tags, org suggestion and
 * embeddings all still run. An earlier draft read "Won't learn filing", which
 * the operator correctly heard as "this email is ignored". Keep the copy
 * pointed at what DOES happen: the filing decision comes from the content.
 */

import type { Email } from '../hooks/useEmails'
import { senderIdentityFor } from './senderIdentity'

/** Copy lives here so the chip text and its tooltip can never drift apart. */
export const SENDER_SIGNAL_COPY = {
  relay: {
    label: 'Relayed',
    title: 'The effective sender was resolved through a known relay.',
  },
  automated: {
    label: 'Filed on content',
    title:
      'Automated sender (a no-reply, notifications, or bulk-mail relay address). ' +
      'This email is still read in full — job numbers, invoice numbers, keywords ' +
      'and codes are all matched as usual. What is skipped is the shortcut that ' +
      'reuses wherever past mail from this address was filed, because one ' +
      'no-reply address covers many jobs and a single misfile would repeat forever.',
  },
} as const

export function SenderSignalBadges({ email }: { email: Email }) {
  const signals = senderIdentityFor(email)?.signals
  if (!signals?.relay && !signals?.automated) return null
  return (
    <>
      {signals.relay && (
        <span
          className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          title={SENDER_SIGNAL_COPY.relay.title}
          data-testid="sender-signal-relay"
        >
          {SENDER_SIGNAL_COPY.relay.label}
        </span>
      )}
      {signals.automated && (
        <span
          className="whitespace-nowrap rounded-full bg-violet-100 px-2 py-0.5 text-[10px] text-violet-700 dark:bg-violet-900/30 dark:text-violet-200"
          title={SENDER_SIGNAL_COPY.automated.title}
          data-testid="sender-signal-automated"
        >
          {SENDER_SIGNAL_COPY.automated.label}
        </span>
      )}
    </>
  )
}

